import { EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import React, { useEffect } from "react";
import { alpha, Box, Theme } from "@mui/material";
import { CustomMention } from "./MentionExtentions.ts";
import { Markdown } from "tiptap-markdown";
import { linkColor } from "./MentionInput.tsx";
import { setEditorContent } from "./MentionInput.hooks.ts";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";

export interface MentionInputRenderProps {
    value: string;
    linkColor?: linkColor;
}

function MentionInputRender(props: MentionInputRenderProps) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4], // Support heading levels 1, 2, 3 and 4
                },
                link: {
                    HTMLAttributes: {
                        class: "mention-link",
                    },
                },
                // Read-only view: the trailing paragraph would only add blank space below
                // the rendered content.
                trailingNode: false,
            }),
            Markdown,
            CustomMention("display", props.linkColor),
            // Without these the schema drops any table authored in MentionInput and only the cell text survives.
            Table.configure({ resizable: false }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: props.value,
        editable: false,
    });

    useEffect(() => {
        if (editor && props.value !== editor.getHTML()) {
            setTimeout(() => {
                setEditorContent(editor, props.value);
            });
        }
    }, [props.value, editor]);

    if (!editor) {
        return null;
    }

    return (
        <Box
            sx={(theme) => ({
                "& .tiptap p": { margin: 0 },
                "& .ProseMirror": {
                    lineHeight: "1.5", // Adjust as needed for consistency
                },
                "& .mention-link": {
                    color: `${getLinkColor(theme, props.linkColor)} !important`,
                    fontWeight: 600,
                    textDecoration: "none !important",
                    cursor: "pointer",
                    "&:hover": {
                        textDecoration: "underline !important",
                    },
                },
                "& .ProseMirror table": {
                    borderCollapse: "collapse",
                    width: "100%",
                    margin: "12px 0",
                    tableLayout: "fixed",
                    overflow: "hidden",
                },
                "& .ProseMirror th, & .ProseMirror td": {
                    border: `1px solid ${getTableBorderColor(theme, props.linkColor)}`,
                    padding: "4px 8px",
                    verticalAlign: "top",
                    textAlign: "left",
                },
                "& .ProseMirror th": {
                    backgroundColor: getTableHeaderColor(theme, props.linkColor),
                    fontWeight: 600,
                },
            })}
        >
            <EditorContent editor={editor} />
        </Box>
    );
}

// A white link colour is how callers say the content sits on a dark tooltip background, where the
// light-theme divider and hover tones used by MentionInput would be invisible.
function isOnDarkBackground(color?: linkColor) {
    return color === "white";
}

function getTableBorderColor(theme: Theme, color?: linkColor) {
    return isOnDarkBackground(color) ? alpha(theme.palette.common.white, 0.4) : theme.palette.divider;
}

function getTableHeaderColor(theme: Theme, color?: linkColor) {
    return isOnDarkBackground(color) ? alpha(theme.palette.common.white, 0.12) : theme.palette.action.hover;
}

function getLinkColor(theme: Theme, color?: linkColor) {
    switch (color) {
        case "primary-dark":
            return theme.palette.primary.dark;
        case "secondary":
            return theme.palette.secondary.main;
        case "white":
            return theme.palette.common.white;
        case "primary":
        default:
            return theme.palette.primary.main;
    }
}

export default MentionInputRender;
